import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import styled from 'styled-components';
import { UserPlus, UserX, UserCheck, Shield, ShieldOff, Trash2, Mail, Pencil } from 'lucide-react';
import { theme } from '@styles/theme';
import { Button, Card, CardContent, Input, Modal, StatusBadge, Spinner, getInvitationStatusType, } from '@components/index';
import { useUsers, usePrototypes } from '@hooks/index';
const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;
const Header = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Title = styled.h1 `
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;
const Section = styled.section `
  margin-bottom: ${theme.spacing.lg};
`;
const SectionTitle = styled.h2 `
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;
const Table = styled.table `
  width: 100%;
  border-collapse: collapse;
`;
const Th = styled.th `
  text-align: left;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.text.muted};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;
const Td = styled.td `
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;
const UserName = styled.span `
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
`;
const UserEmail = styled.span `
  display: block;
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.muted};
`;
const RoleBadge = styled.span `
  display: inline-block;
  padding: 2px ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.medium};
  border-radius: ${theme.borderRadius.full};
  background-color: ${({ $role }) => $role === 'admin' ? `${theme.colors.status.warning}20` : `${theme.colors.status.info}20`};
  color: ${({ $role }) => ($role === 'admin' ? theme.colors.status.warning : theme.colors.status.info)};
`;
const Form = styled.form `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;
const CheckboxGroup = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;
const CheckboxLabel = styled.label `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
  }
`;
const LoadingContainer = styled.div `
  display: flex;
  justify-content: center;
  padding: ${theme.spacing.xl};
`;
const EmptyState = styled.div `
  text-align: center;
  padding: ${theme.spacing.lg};
  color: ${theme.colors.text.muted};
  font-size: ${theme.typography.fontSize.sm};
`;
const ErrorMessage = styled.div `
  color: ${theme.colors.status.error};
  font-size: ${theme.typography.fontSize.sm};
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.status.error}20;
  border-radius: ${theme.borderRadius.md};
`;
const Label = styled.label `
  display: block;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.xs};
`;
const ActionsContainer = styled.div `
  display: flex;
  gap: ${theme.spacing.xs};
  align-items: center;
`;
export function AdminUsersPage() {
    const { users, invitations, isLoading, error, inviteUser, getUserAccess, updateUserAccess, deactivateUser, reactivateUser, updateUserRole, deleteUser, revokeInvitation } = useUsers();
    const { prototypes } = usePrototypes();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formEmail, setFormEmail] = useState('');
    const [selectedPrototypes, setSelectedPrototypes] = useState([]);
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deactivatingId, setDeactivatingId] = useState(null);
    const [reactivatingId, setReactivatingId] = useState(null);
    const [updatingRoleId, setUpdatingRoleId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [revokingId, setRevokingId] = useState(null);
    const [accessUser, setAccessUser] = useState(null);
    const [accessSelected, setAccessSelected] = useState([]);
    const [accessError, setAccessError] = useState(null);
    const [isAccessLoading, setIsAccessLoading] = useState(false);
    const [isAccessSubmitting, setIsAccessSubmitting] = useState(false);
    const handleOpenModal = () => {
        setIsModalOpen(true);
        setFormError(null);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setFormEmail('');
        setSelectedPrototypes([]);
        setFormError(null);
    };
    const handlePrototypeToggle = (prototypeId) => {
        setSelectedPrototypes((prev) => prev.includes(prototypeId) ? prev.filter((id) => id !== prototypeId) : [...prev, prototypeId]);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        if (selectedPrototypes.length === 0) {
            setFormError('Please select at least one prototype');
            return;
        }
        setIsSubmitting(true);
        try {
            await inviteUser(formEmail, selectedPrototypes);
            handleCloseModal();
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to send invitation';
            setFormError(message);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleDeactivate = async (user) => {
        if (!window.confirm(`Are you sure you want to deactivate ${user.name}?`)) {
            return;
        }
        setDeactivatingId(user.id);
        try {
            await deactivateUser(user.id);
        }
        catch {
            // Error handling could be improved with toast notifications
        }
        finally {
            setDeactivatingId(null);
        }
    };
    const handleReactivate = async (user) => {
        if (!window.confirm(`Are you sure you want to reactivate ${user.name}?`)) {
            return;
        }
        setReactivatingId(user.id);
        try {
            await reactivateUser(user.id);
        }
        catch {
            // Error handling could be improved with toast notifications
        }
        finally {
            setReactivatingId(null);
        }
    };
    const handlePromoteToAdmin = async (user) => {
        if (!window.confirm(`Are you sure you want to promote ${user.name} to Admin?`)) {
            return;
        }
        setUpdatingRoleId(user.id);
        try {
            await updateUserRole(user.id, 'admin');
        }
        catch {
            // Error handling could be improved with toast notifications
        }
        finally {
            setUpdatingRoleId(null);
        }
    };
    const handleDemoteToClient = async (user) => {
        if (!window.confirm(`Are you sure you want to demote ${user.name} to Client?`)) {
            return;
        }
        setUpdatingRoleId(user.id);
        try {
            await updateUserRole(user.id, 'client');
        }
        catch {
            // Error handling could be improved with toast notifications
        }
        finally {
            setUpdatingRoleId(null);
        }
    };
    const handleDelete = async (user) => {
        if (!window.confirm(`Are you sure you want to permanently delete ${user.name}? This action cannot be undone.`)) {
            return;
        }
        setDeletingId(user.id);
        try {
            await deleteUser(user.id);
        }
        catch {
            // Error handling could be improved with toast notifications
        }
        finally {
            setDeletingId(null);
        }
    };
    const handleRevoke = async (invitation) => {
        if (!window.confirm(`Are you sure you want to revoke the invitation for ${invitation.email}?`)) {
            return;
        }
        setRevokingId(invitation.id);
        try {
            await revokeInvitation(invitation.id);
        }
        catch {
            // Error handling could be improved with toast notifications
        }
        finally {
            setRevokingId(null);
        }
    };
    const handleOpenAccessModal = async (user) => {
        setAccessUser(user);
        setAccessSelected([]);
        setAccessError(null);
        setIsAccessLoading(true);
        try {
            const ids = await getUserAccess(user.id);
            setAccessSelected(ids);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load current access';
            setAccessError(message);
        }
        finally {
            setIsAccessLoading(false);
        }
    };
    const handleCloseAccessModal = () => {
        setAccessUser(null);
        setAccessSelected([]);
        setAccessError(null);
    };
    const handleAccessToggle = (prototypeId) => {
        setAccessSelected((prev) => prev.includes(prototypeId) ? prev.filter((id) => id !== prototypeId) : [...prev, prototypeId]);
    };
    const handleAccessSubmit = async (e) => {
        e.preventDefault();
        if (!accessUser)
            return;
        setAccessError(null);
        setIsAccessSubmitting(true);
        try {
            await updateUserAccess(accessUser.id, accessSelected);
            handleCloseAccessModal();
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update access';
            setAccessError(message);
        }
        finally {
            setIsAccessSubmitting(false);
        }
    };
    if (isLoading) {
        return (_jsx(LoadingContainer, { children: _jsx(Spinner, { size: "lg" }) }));
    }
    if (error) {
        return _jsx(ErrorMessage, { children: error });
    }
    const pendingInvitations = invitations.filter((i) => i.status === 'pending');
    const allUsers = users; // Show all users (both admin and client)
    return (_jsxs(Container, { children: [_jsxs(Header, { children: [_jsx(Title, { children: "Users" }), _jsxs(Button, { onClick: handleOpenModal, children: [_jsx(UserPlus, { size: 18 }), "Invite User"] })] }), _jsxs(Section, { children: [_jsxs(SectionTitle, { children: ["All Users (", allUsers.length, ")"] }), _jsx(Card, { children: _jsx(CardContent, { children: allUsers.length === 0 ? (_jsx(EmptyState, { children: "No users yet. Send an invitation to add users." })) : (_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "User" }), _jsx(Th, { children: "Role" }), _jsx(Th, { children: "Status" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: allUsers.map((user) => (_jsxs("tr", { children: [_jsxs(Td, { children: [_jsx(UserName, { children: user.name }), _jsx(UserEmail, { children: user.email })] }), _jsx(Td, { children: _jsx(RoleBadge, { "$role": user.role, children: user.role }) }), _jsx(Td, { children: _jsx(StatusBadge, { status: user.isActive ? 'success' : 'neutral', size: "sm", children: user.isActive ? 'Active' : 'Inactive' }) }), _jsx(Td, { children: _jsx(ActionsContainer, { children: user.isActive ? (_jsxs(_Fragment, { children: [_jsxs(Button, { variant: "secondary", size: "sm", onClick: () => user.role === 'client' ? handlePromoteToAdmin(user) : handleDemoteToClient(user), disabled: updatingRoleId === user.id, loading: updatingRoleId === user.id, title: user.role === 'client' ? 'Promote to Admin' : 'Demote to Client', children: [user.role === 'client' ? _jsx(Shield, { size: 14 }) : _jsx(ShieldOff, { size: 14 }), user.role === 'client' ? 'Make Admin' : 'Make Client'] }), user.role === 'client' && (_jsxs(Button, { variant: "secondary", size: "sm", onClick: () => handleOpenAccessModal(user), disabled: isAccessLoading && accessUser?.id === user.id, loading: isAccessLoading && accessUser?.id === user.id, title: "Edit which prototypes this user can access", children: [_jsx(Pencil, { size: 14 }), "Edit Access"] })), _jsxs(Button, { variant: "danger", size: "sm", onClick: () => handleDeactivate(user), disabled: deactivatingId === user.id, loading: deactivatingId === user.id, children: [_jsx(UserX, { size: 14 }), "Deactivate"] }), _jsxs(Button, { variant: "danger", size: "sm", onClick: () => handleDelete(user), disabled: deletingId === user.id, loading: deletingId === user.id, title: "Permanently delete user", children: [_jsx(Trash2, { size: 14 }), "Delete"] })] })) : (_jsxs(Button, { variant: "secondary", size: "sm", onClick: () => handleReactivate(user), disabled: reactivatingId === user.id, loading: reactivatingId === user.id, children: [_jsx(UserCheck, { size: 14 }), "Reactivate"] })) }) })] }, user.id))) })] })) }) })] }), _jsxs(Section, { children: [_jsx(SectionTitle, { children: "Pending Invitations" }), _jsx(Card, { children: _jsx(CardContent, { children: pendingInvitations.length === 0 ? (_jsx(EmptyState, { children: "No pending invitations" })) : (_jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Email" }), _jsx(Th, { children: "Status" }), _jsx(Th, { children: "Expires" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: pendingInvitations.map((invitation) => (_jsxs("tr", { children: [_jsxs(Td, { children: [_jsx(Mail, { size: 14, style: { marginRight: '8px', verticalAlign: 'middle' } }), invitation.email] }), _jsx(Td, { children: _jsx(StatusBadge, { status: getInvitationStatusType(invitation.status), size: "sm", children: invitation.status }) }), _jsx(Td, { children: new Date(invitation.expiresAt).toLocaleDateString() }), _jsx(Td, { children: _jsx(Button, { variant: "danger", size: "sm", onClick: () => handleRevoke(invitation), disabled: revokingId === invitation.id, loading: revokingId === invitation.id, children: "Revoke" }) })] }, invitation.id))) })] })) }) })] }), _jsx(Modal, { isOpen: isModalOpen, onClose: handleCloseModal, title: "Invite User", footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: handleCloseModal, children: "Cancel" }), _jsx(Button, { type: "submit", form: "invite-form", loading: isSubmitting, disabled: isSubmitting, children: "Send Invitation" })] }), children: _jsxs(Form, { id: "invite-form", onSubmit: handleSubmit, children: [formError && _jsx(ErrorMessage, { children: formError }), _jsx(Input, { label: "Email Address", type: "email", value: formEmail, onChange: (e) => setFormEmail(e.target.value), placeholder: "user@example.com", required: true }), _jsxs("div", { children: [_jsx(Label, { children: "Prototype Access" }), prototypes.length === 0 ? (_jsx(EmptyState, { children: "No prototypes available. Register a prototype first." })) : (_jsx(CheckboxGroup, { children: prototypes.map((prototype) => (_jsxs(CheckboxLabel, { children: [_jsx("input", { type: "checkbox", checked: selectedPrototypes.includes(prototype.id), onChange: () => handlePrototypeToggle(prototype.id) }), prototype.name] }, prototype.id))) }))] })] }) }), _jsx(Modal, { isOpen: accessUser !== null, onClose: handleCloseAccessModal, title: accessUser ? `Edit Access — ${accessUser.name}` : 'Edit Access', footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: handleCloseAccessModal, children: "Cancel" }), _jsx(Button, { type: "submit", form: "edit-access-form", loading: isAccessSubmitting, disabled: isAccessSubmitting || isAccessLoading, children: "Save Access" })] }), children: accessUser && (_jsxs(Form, { id: "edit-access-form", onSubmit: handleAccessSubmit, children: [accessError && _jsx(ErrorMessage, { children: accessError }), _jsxs("div", { children: [_jsx(Label, { children: "Accessible Prototypes" }), isAccessLoading ? (_jsx(LoadingContainer, { children: _jsx(Spinner, { size: "sm" }) })) : prototypes.length === 0 ? (_jsx(EmptyState, { children: "No prototypes available. Register a prototype first." })) : (_jsx(CheckboxGroup, { children: prototypes.map((prototype) => (_jsxs(CheckboxLabel, { children: [_jsx("input", { type: "checkbox", checked: accessSelected.includes(prototype.id), onChange: () => handleAccessToggle(prototype.id), "aria-label": prototype.name }), prototype.name] }, prototype.id))) }))] })] })) })] }));
}
